import type { TurnUsage, ModelUsage } from '@lens/types/claude'

interface ModelPricing {
  input: number
  output: number
  cacheWrite: number
  cacheRead: number
}

// ─── Model list prices (per-million-token USD, divided to per-token here) ──────
//
// Verified 2026-07 public list rates. Convention: cacheWrite = 1.25× input,
// cacheRead = 0.1× input (Anthropic's prompt-caching multipliers). The model
// ids are the exact strings the Claude reader emits — `session.model` /
// `stats-cache.json` modelUsage keys — so the exact-match branch in getPricing()
// hits before the fuzzy fallback (which is what silently mispriced these before).
//
// IMPORTANT (fixed 2026-07): the 2026 families (opus-4-8, opus-4-7, fable-5) were
// MISSING here. `claude-opus-4-8`/`-4-7` fuzzy-matched the stale `claude-opus-4-6`
// $15/$75 line (via the `slice(0,3)` prefix branch), and `claude-fable-5` — which
// prefix-matches nothing — fell all the way through to the $15/$75 fallback. Net
// effect: a ~3× overcount on Opus (the dominant model in the logs) and a
// wrong-tier read on Fable. The corrected Opus rate is $5/$25; Fable is $10/$50.
export const PRICING: Record<string, ModelPricing> = {
  // ── 2026-07 families (the current defaults) ───────────────────────────────
  // Opus 4.8 / 4.7 — corrected rate $5/$25 (NOT the stale opus-4-6 $15/$75).
  // The `[1m]` 1M-context id variant is normalized off in getPricing() so it
  // shares this rate.
  'claude-opus-4-8': {
    input:       5.00 / 1_000_000,
    output:     25.00 / 1_000_000,
    cacheWrite:  6.25 / 1_000_000,
    cacheRead:   0.50 / 1_000_000,
  },
  'claude-opus-4-7': {
    input:       5.00 / 1_000_000,
    output:     25.00 / 1_000_000,
    cacheWrite:  6.25 / 1_000_000,
    cacheRead:   0.50 / 1_000_000,
  },
  // Fable 5 — a tier ABOVE Opus at $10/$50 (this is the surprise the corrected
  // table surfaces: Fable is dearer than Opus, per PROTO_PRICING).
  'claude-fable-5': {
    input:      10.00 / 1_000_000,
    output:     50.00 / 1_000_000,
    cacheWrite: 12.50 / 1_000_000,
    cacheRead:   1.00 / 1_000_000,
  },
  // Sonnet 5 — $2/$10 (the id appears in the logs; without this it fuzzy-matched
  // the sonnet-4-6 $3/$15 line). NOTE: $2/$10 is the INTRODUCTORY rate, in effect
  // through 2026-08-31; the list rate is $3/$15 — bump this entry when it lapses.
  'claude-sonnet-5': {
    input:       2.00 / 1_000_000,
    output:     10.00 / 1_000_000,
    cacheWrite:  2.50 / 1_000_000,
    cacheRead:   0.20 / 1_000_000,
  },
  // ── prior families (retained so historical sessions still price correctly) ──
  // Opus 4.6 and 4.5 are $5/$25 — the Opus price drop began at 4.5 (2025-11);
  // only 4.1 and older carry the legacy $15/$75 premium tier.
  'claude-opus-4-6': {
    input:       5.00 / 1_000_000,
    output:     25.00 / 1_000_000,
    cacheWrite:  6.25 / 1_000_000,
    cacheRead:   0.50 / 1_000_000,
  },
  'claude-opus-4-5-20251101': {
    input:       5.00 / 1_000_000,
    output:     25.00 / 1_000_000,
    cacheWrite:  6.25 / 1_000_000,
    cacheRead:   0.50 / 1_000_000,
  },
  // Opus 4.1 — legacy premium tier at $15/$75. Pinned explicitly so its id
  // (present in historical stats-cache modelUsage) prices at its OWN historical
  // rate rather than fuzzy-matching the new $5/$25 opus-4-8 line.
  'claude-opus-4-1-20250805': {
    input:      15.00 / 1_000_000,
    output:     75.00 / 1_000_000,
    cacheWrite: 18.75 / 1_000_000,
    cacheRead:   1.50 / 1_000_000,
  },
  'claude-sonnet-4-6': {
    input:       3.00 / 1_000_000,
    output:     15.00 / 1_000_000,
    cacheWrite:  3.75 / 1_000_000,
    cacheRead:   0.30 / 1_000_000,
  },
  // Haiku 4.5 is $1/$5 (the $0.80/$4 it previously carried is Haiku 3.5's rate).
  'claude-haiku-4-5': {
    input:       1.00 / 1_000_000,
    output:      5.00 / 1_000_000,
    cacheWrite:  1.25 / 1_000_000,
    cacheRead:   0.10 / 1_000_000,
  },
}

// Fallback for a genuinely-unknown model id (one not in the table and not a
// prefix/suffix variant of a known one). Deliberately the CURRENT-generation
// Opus rate ($5/$25) — an unknown id today is far likelier a new default-tier
// model than a legacy premium one, so this fails toward the conservative
// current rate rather than the old $15/$75 fallback's ~3× overcount.
const FALLBACK_PRICING: ModelPricing = {
  input:       5.00 / 1_000_000,
  output:     25.00 / 1_000_000,
  cacheWrite:  6.25 / 1_000_000,
  cacheRead:   0.50 / 1_000_000,
}

function getPricing(model: string): ModelPricing {
  const exact = PRICING[model]
  if (exact) return exact
  // Normalize a trailing context-window tag, e.g. `claude-opus-4-8[1m]`, then
  // retry the exact match — the [1m] variant is the same price as its base id.
  const base = model.replace(/\[[^\]]*\]$/, '')
  if (base !== model) {
    const baseHit = PRICING[base]
    if (baseHit) return baseHit
  }
  // fuzzy match on prefix
  for (const key of Object.keys(PRICING)) {
    if (base.startsWith(key) || key.startsWith(base.split('-').slice(0, 3).join('-'))) {
      const hit = PRICING[key]
      if (hit) return hit
    }
  }
  return FALLBACK_PRICING
}

export function estimateCostFromUsage(model: string, usage: TurnUsage): number {
  const p = getPricing(model)
  return (
    (usage.input_tokens                ?? 0) * p.input      +
    (usage.output_tokens               ?? 0) * p.output     +
    (usage.cache_creation_input_tokens ?? 0) * p.cacheWrite +
    (usage.cache_read_input_tokens     ?? 0) * p.cacheRead
  )
}

export function estimateCostFromSessionMeta(
  model: string,
  inputTokens: number,
  outputTokens: number,
): number {
  const p = getPricing(model)
  return inputTokens * p.input + outputTokens * p.output
}

export interface CacheEfficiencyResult {
  savedUSD: number
  hitRate: number
  wouldHavePaidUSD: number
}

export function cacheEfficiency(
  model: string,
  usage: ModelUsage,
): CacheEfficiencyResult {
  const p = getPricing(model)
  const savedPerToken = p.input - p.cacheRead
  const savedUSD = usage.cacheReadInputTokens * savedPerToken
  const totalContext = usage.inputTokens + usage.cacheReadInputTokens
  const hitRate = totalContext > 0
    ? usage.cacheReadInputTokens / totalContext
    : 0
  const wouldHavePaidUSD =
    (usage.inputTokens + usage.cacheReadInputTokens) * p.input +
    usage.outputTokens * p.output +
    usage.cacheCreationInputTokens * p.cacheWrite
  return { savedUSD, hitRate, wouldHavePaidUSD }
}

export function estimateTotalCostFromModel(model: string, usage: ModelUsage): number {
  const p = getPricing(model)
  return (
    (usage.inputTokens                ?? 0) * p.input      +
    (usage.outputTokens               ?? 0) * p.output     +
    (usage.cacheCreationInputTokens   ?? 0) * p.cacheWrite +
    (usage.cacheReadInputTokens       ?? 0) * p.cacheRead
  )
}

export { getPricing }
export type { ModelPricing }
